import { 
  assertUint8Arrays,
  hashUint8Array,
  getFromStore,
  putToStore, 
  getAllFromIndex,
  openDB, } from "./Utils";
import {
  encryptData,
  decryptData,
  deriveKeyFromPassword,
} from "../utils/CryptoHelpers";
import { loadLocalKeys } from "./KeyStorageManager";
import { DB_NAME, DB_VERSION, setupSchema } from "./Schema";
import { RATCHETING } from '../Config';
import bs58 from "bs58";
import { sha256 } from 'js-sha256';

async function getDB() {
  return await openDB(DB_NAME, DB_VERSION, setupSchema);
}

/**
 * Internal helper to fetch and decrypt a ratcheting record for a conversation+participant.
 * Returns a default structure if no record exists.
 *
 * @param {Object} params
 * @param {Uint8Array} params.conversationId
 * @param {Uint8Array} params.contactIdentityViewPublicKey
 * @param {string} params.userId
 * @param {string} params.password
 * @returns {Promise<{ data: Object, salt?: Uint8Array, aesKey?: CryptoKey }>}
 */
async function getDecryptedEntry({
  conversationId,
  contactIdentityViewPublicKey,
  userId,
  password,
}) {
  assertUint8Arrays({ conversationId, contactIdentityViewPublicKey });

  const db = await getDB();
  const hashedConversationId = await hashUint8Array(conversationId);
  const contactKeyB58 = bs58.encode(contactIdentityViewPublicKey);
  const userKeyHash = await sha256(userId + password);
  const compoundKey = `${userKeyHash}::${hashedConversationId}::${contactKeyB58}`;

  const tx = db.transaction(RATCHETING.STORE_NAME, "readonly");
  const store = tx.objectStore(RATCHETING.STORE_NAME);

  const record = await getFromStore(store, compoundKey);

  if (!record) {
    return {
      data: {
        contactIdentityViewPublicKey,
        contactNextViewPublicKey: undefined,
        localViewKeys: [],
        sessionKyberKeys: [],
        updatedAt: undefined,
      },
    };
  }

  return await decryptRatchetingRecord(record, password);
}

/**
 * Decrypts a ratcheting record and returns its parsed and decoded structure.
 * Converts base58 strings back to Uint8Arrays for all key fields.
 *
 * @param {Object} record - The raw DB record (with `.encrypted` and `.salt`)
 * @param {string} password - The decryption password
 * @returns {Promise<{ data: Object, aesKey: CryptoKey }>}
 */
async function decryptRatchetingRecord(record, password) {
  const salt = Uint8Array.from(atob(record.salt), c => c.charCodeAt(0));
  const aesKey = await deriveKeyFromPassword(password, salt);
  const decrypted = await decryptData(record.encrypted, aesKey);
  const parsed = JSON.parse(decrypted) || {};

  const decodeIfExists = (v) => (v ? bs58.decode(v) : undefined);

  const data = {
    contactIdentityViewPublicKey: decodeIfExists(parsed.contactIdentityViewPublicKey),
    contactNextViewPublicKey: decodeIfExists(parsed.contactNextViewPublicKey),
    localViewKeys: Array.isArray(parsed.localViewKeys)
      ? parsed.localViewKeys.map((key) => ({
          viewPublicKey: decodeIfExists(key.viewPublicKey),
          viewPrivateKey: decodeIfExists(key.viewPrivateKey),
        }))
      : [],
    sessionKyberKeys: Array.isArray(parsed.sessionKyberKeys)
      ? parsed.sessionKyberKeys.map((key) => ({
          kyberPublicKey: decodeIfExists(key.kyberPublicKey),
          kyberPrivateKey: decodeIfExists(key.kyberPrivateKey),
          kyberSecret: decodeIfExists(key.kyberSecret),
        }))
      : [],
    updatedAt: parsed.updatedAt || undefined,
  };

  return data;
}

/**
 * Inserts or updates the ratcheted key state for a specific participant in a conversation.
 * 
 * - Maintains isolated ratcheting state per (userId + conversationId + contactIdentityViewPublicKey).
 * - Creates a new entry if none exists.
 * - Updates ratcheted fields if new values are provided.
 * - Appends new local ratcheted keys (capped at MAX entries).
 * - All Uint8Array fields are base58-encoded before encryption.
 * 
 * Call this once per participant to support multi-member conversations.
 */
export async function upsertRatchetKeys({
  conversationId,
  contactIdentityViewPublicKey,
  contactNextViewPublicKey,
  localNextViewPublicKey,
  localNextViewPrivateKey,
  sessionNextKyberPublicKey,
  sessionNextKyberPrivateKey,
  sessionNextKyberSecret,
  userId,
  password,
}) {
  // Validate binary inputs
  assertUint8Arrays({
    conversationId,
    contactIdentityViewPublicKey,
    contactNextViewPublicKey,
    localNextViewPublicKey,
    localNextViewPrivateKey,
    sessionNextKyberPublicKey,
    sessionNextKyberPrivateKey,
    sessionNextKyberSecret,
  });

  const db = await getDB();
  const hashedConversationId = await hashUint8Array(conversationId);
  const contactKeyB58 = bs58.encode(contactIdentityViewPublicKey);
  const userKeyHash = sha256(userId + password);
  const compoundKey = `${userKeyHash}::${hashedConversationId}::${contactKeyB58}`;

  const tx = db.transaction(RATCHETING.STORE_NAME, 'readonly');
  const store = tx.objectStore(RATCHETING.STORE_NAME);
  const existingRecord = await getFromStore(store, compoundKey);

  let entry;
  let salt;

  if (existingRecord) {
    salt = Uint8Array.from(atob(existingRecord.salt), c => c.charCodeAt(0));
    const aesKey = await deriveKeyFromPassword(password, salt);
    const decrypted = await decryptData(existingRecord.encrypted, aesKey);
    entry = JSON.parse(decrypted);
  } else {
    salt = crypto.getRandomValues(new Uint8Array(16));
    entry = {
      contactIdentityViewPublicKey: undefined,
      contactNextViewPublicKey: undefined,
      localViewKeys: [],
      sessionKyberKeys: [],
      updatedAt: new Date().toISOString(),
    };
  }

  if (contactIdentityViewPublicKey != null) {
    entry.contactIdentityViewPublicKey = bs58.encode(contactIdentityViewPublicKey);
  }
  if (contactNextViewPublicKey != null) {
    entry.contactNextViewPublicKey = bs58.encode(contactNextViewPublicKey);
  }

  if (localNextViewPublicKey && localNextViewPrivateKey) {
    entry.localViewKeys = [
      ...entry.localViewKeys,
      {
        viewPublicKey: bs58.encode(localNextViewPublicKey),
        viewPrivateKey: bs58.encode(localNextViewPrivateKey),
      },
    ].slice(-RATCHETING.MAX_LOCAL_VIEW_KEYS);
  }

  // Update or insert local Kyber keypair (with optional secret)
  if (sessionNextKyberPublicKey) {
    const pubB58 = bs58.encode(sessionNextKyberPublicKey);
    const privB58 = sessionNextKyberPrivateKey ? bs58.encode(sessionNextKyberPrivateKey) : null;
    const secretB58 = sessionNextKyberSecret ? bs58.encode(sessionNextKyberSecret) : null;

    // Look for existing keypair by public key only
    const existingIndex = entry.sessionKyberKeys.findIndex(
      (k) => k.kyberPublicKey === pubB58
    );

    if (existingIndex !== -1) {
      // Update existing fields only if new values are provided
      if (privB58) {
        entry.sessionKyberKeys[existingIndex].kyberPrivateKey = privB58;
      }
      if (secretB58) {
        entry.sessionKyberKeys[existingIndex].kyberSecret = secretB58;
      }
    } else {
      // Append new entry
      entry.sessionKyberKeys = [
        ...entry.sessionKyberKeys,
        {
          kyberPublicKey: pubB58,
          ...(privB58 && { kyberPrivateKey: privB58 }),
          ...(secretB58 && { kyberSecret: secretB58 }),
        },
      ].slice(-RATCHETING.MAX_LOCAL_KYBER_KEYS);
    }
  }

  entry.updatedAt = new Date().toISOString();

  const aesKey = await deriveKeyFromPassword(password, salt);
  const encrypted = await encryptData(JSON.stringify(entry), aesKey);
  const saltB64 = btoa(String.fromCharCode(...salt));

  const writeTx = db.transaction(RATCHETING.STORE_NAME, 'readwrite');
  const writeStore = writeTx.objectStore(RATCHETING.STORE_NAME);

  await putToStore(writeStore, {
    compoundKey,
    userKeyHash,
    hashedConversationId,
    contactKeyB58,
    encrypted,
    salt: saltB64,
  });
}

/**
 * Returns array of local view keypairs for the conversation.
 */
export async function getRatchetingKeys({  
  conversationId,
  contactIdentityViewPublicKey,
  userId,
  password,
}) {
  return await getDecryptedEntry({  
    conversationId,
    contactIdentityViewPublicKey,
    userId,
    password,
  });
}

/**
 * Returns a flat array of all ratcheting records tied to the user,
 * enriched with fallback kyber secrets when necessary.
 */
export async function getAllLocalRatchetingKeys({ userId, password, salt }) {
  const db = await getDB();
  const index = db.transaction(RATCHETING.STORE_NAME, "readonly")
                  .objectStore(RATCHETING.STORE_NAME)
                  .index("byUserKeyHash");

  const userKeyHash = await sha256(userId + password);
  const records = await getAllFromIndex(index, userKeyHash);
  const unlinkKeys = await loadLocalKeys({ userId, password, salt });
  const allKeys = [];
  
  for (const record of records) {
    try {
      const { 
        localViewKeys,
        sessionKyberKeys,
      } = await decryptRatchetingRecord(record, password);

      allKeys.push({
        /*localViewKeys: [...localViewKeys, ...[{
          viewPublicKey: unlinkKeys.viewPublicKey,
          viewPrivateKey: unlinkKeys.viewPrivateKey,
        }]],*/
        localViewKeys,
        sessionKyberKeys,
      });
    } catch (e) {
      console.warn("Failed to decrypt ratcheting record:", e);
    }
  }

  // Add unlink root keypair
  if (unlinkKeys?.viewPublicKey && unlinkKeys?.viewPrivateKey) {
    allKeys.push({
      localViewKeys: [{
        viewPublicKey: unlinkKeys.viewPublicKey,
        viewPrivateKey: unlinkKeys.viewPrivateKey,
      }],
      sessionKyberKeys: [],
    });
  }

  return allKeys;
}

/**
 * Removes a conversation key record from the DB by conversationId.
 */
export async function deleteConversationKeyRecord(conversationId) {
  const db = await getDB();
  const hashedConversationId = await hashUint8Array(conversationId);
  await db.delete(RATCHETING.STORE_NAME, hashedConversationId);
}